import React, { PureComponent } from 'react';
import { Flex } from '../../../common/Flex';
import { CountsGraph } from '../../../panels/tv/graph/CountsGraph';
import { EntryLoadBar } from '../flows/svg/EntryLoadBar';
import { ExtendedMonitoredTv } from '../../../../model/SOL49';
import {
    CountType,
    FlightId,
    RegulationDto,
    TimePeriod,
    TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
} from '../../../../model/CORE';
import Utils, { ALT_MASK, CTRL_MASK, SHIFT_MASK } from '../../../../common/util/Utils';
import TimePeriodUtils from '../../../../common/util/TimePeriodUtils';
import { Dropdown, Menu } from 'antd';
import MeasureUtils from '../../../../common/util/MeasureUtils';
import { CloseOutlined } from '@ant-design/icons/lib';
import { ModalHandler } from '../../../panels/ModalHandler';
import { RegulationPeriods } from '../../../../model/client/CORE';
import './AtcSector.less';
import { OccupancyLoadBar } from '../flows/svg/OccupancyLoadBar';

interface AtcSectorProps {
    currentTime: Date;
    countType: CountType;
    monitoredTv: ExtendedMonitoredTv;
    openingPeriod: TimePeriod;
    occupancyPeriods?: Map<FlightId, TimePeriod>;
    regulationPeriods?: Array<RegulationPeriods>;
    proposalRegulationPeriods?: Array<RegulationPeriods>;
    selectedPeriodByTv?: Map<string, Array<TimePeriod>>;
    onSelectedPeriodByTvChange?: (selectedPeriodByTv: Map<string, Array<TimePeriod>>) => void;
}

export class AtcSector extends PureComponent<AtcSectorProps> {
    handleSelectedPeriodChange = (tvId: string) => {
        return (selectedPeriod: TimePeriod, bitMask?: number) => {
            const shiftKey = bitMask & SHIFT_MASK;
            const ctrlKey = bitMask & CTRL_MASK;
            const altKey = bitMask & ALT_MASK;

            // Keep previous values if CTRL key pressed
            const values =
                ctrlKey && this.props.selectedPeriodByTv ? this.props.selectedPeriodByTv.entries() : undefined;

            const selectedPeriodByTv = new Map<string, Array<TimePeriod>>(values);

            // Only keep periods outside of opening period of ATC sector, allowing a change of selection in the sector
            const tvPeriods = (selectedPeriodByTv.get(tvId) || []).filter(
                period => !TimePeriodUtils.intersects(this.props.openingPeriod, period),
            );
            tvPeriods.push(selectedPeriod);
            selectedPeriodByTv.set(tvId, tvPeriods);

            this.props.onSelectedPeriodByTvChange?.(selectedPeriodByTv);
        };
    };

    handleCreateRegulation = () => {
        const monitoredTv = this.props.monitoredTv;
        const tvId = monitoredTv.trafficCounts.tvId;
        const selectedPeriod = this.props.selectedPeriodByTv
            ?.get(tvId)
            ?.find(timePeriod => TimePeriodUtils.intersects(timePeriod, this.props.openingPeriod));
        const newRegulationPeriod = selectedPeriod || this.props.openingPeriod;

        const initialRegulation: Partial<RegulationDto> = {
            applicability: newRegulationPeriod,
            location: {
                id: tvId,
                clazz: TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
            },
            initialConstraints: MeasureUtils.hourlyEntryCapacityToRegInitialConstraints(
                monitoredTv.trafficCounts.hourlyEntryCapacity,
                newRegulationPeriod,
            ),
        };
        ModalHandler.showRegulationEditor(tvId, 'tv', initialRegulation);
    };

    handleShowDetails = () => {
        const monitoredTv = this.props.monitoredTv;
        const tvId = monitoredTv.trafficCounts.tvId;
        const regulationPeriods = this.props.regulationPeriods;
        ModalHandler.showTvDetails(tvId, undefined, regulationPeriods, this.props.occupancyPeriods);
    };

    getOverlayMenu = () => {
        const monitoredTv = this.props.monitoredTv;
        const tvId = monitoredTv.trafficCounts.tvId;
        return (
            <Menu>
                <Menu.Item key="close" style={{ backgroundColor: '#313131' }}>
                    <Flex>
                        <Flex style={{ color: 'white', fontWeight: 'bold' }}>{tvId}</Flex>
                        <Flex grow={false}>
                            <CloseOutlined />
                        </Flex>
                    </Flex>
                </Menu.Item>
                <Menu.Item key="show-details" onClick={this.handleShowDetails}>
                    Show details (graphs)
                </Menu.Item>
                <Menu.Item key="create-regulation" onClick={this.handleCreateRegulation}>
                    Create a new regulation
                </Menu.Item>
            </Menu>
        );
    };

    render() {
        const monitoredTv = this.props.monitoredTv;
        const tvId = monitoredTv.trafficCounts.tvId;
        const selectedPeriod = this.props.selectedPeriodByTv
            ?.get(tvId)
            ?.find(timePeriod => TimePeriodUtils.intersects(timePeriod, this.props.openingPeriod));
        const shortSectorName = tvId.substring(3, tvId.length);

        let counts, hourlyEntryCapacity, otmvDefns;
        let loadBarComponent;
        switch (this.props.countType) {
            case CountType.ENTRY:
                hourlyEntryCapacity = monitoredTv.trafficCounts.hourlyEntryCapacity;
                counts = monitoredTv.trafficCounts.entryCounts;
                loadBarComponent = (
                    <EntryLoadBar
                        hideLabel={true}
                        drawBottomLine={true}
                        loadValues={monitoredTv.loadValues}
                        currentTime={this.props.currentTime}
                        monitoredPeriod={this.props.openingPeriod}
                        activePeriods={[this.props.openingPeriod]}
                        barHeightEm={0.5}
                    />
                );
                break;

            case CountType.OCCUPANCY:
                counts = monitoredTv.trafficCounts.occupancyCounts?.[0];
                if (counts) {
                    const duration = counts.timeRange.countDurationInMinutes;
                    otmvDefns = monitoredTv.trafficCounts.otmvDefns?.[duration];

                    const loadValues = monitoredTv.occupancyLoadValues.find(
                        occLoadValues => occLoadValues.durationInMin === duration,
                    );
                    loadBarComponent = (
                        <OccupancyLoadBar
                            hideLabel={true}
                            drawBottomLine={true}
                            loadValues={loadValues?.occupancyLoadValues}
                            currentTime={this.props.currentTime}
                            monitoredPeriod={this.props.openingPeriod}
                            activePeriods={[this.props.openingPeriod]}
                            barHeightEm={0.5}
                        />
                    );
                }
                break;
        }

        if (Utils.isNotExists(counts)) return undefined;

        return (
            <Dropdown overlay={this.getOverlayMenu} trigger={['contextMenu']}>
                <Flex className="atc-sector" direction="vertical" style={{ minHeight: '10em' }}>
                    <Flex className="graph">
                        <Flex className="label">{shortSectorName}</Flex>
                        <Flex>
                            <CountsGraph
                                currentTime={this.props.currentTime}
                                counts={counts.flightModelCounts}
                                timeRange={counts.timeRange}
                                hourlyEntryCapacity={hourlyEntryCapacity}
                                otmvDefns={otmvDefns}
                                regulationPeriods={this.props.regulationPeriods}
                                proposalRegulationPeriods={this.props.proposalRegulationPeriods}
                                activePeriods={[this.props.openingPeriod]}
                                highlightedFlightsOccupancy={this.props.occupancyPeriods}
                                displayPeriod={this.props.openingPeriod}
                                selectedPeriod={selectedPeriod}
                                onSelectedPeriodChange={this.handleSelectedPeriodChange(tvId)}
                                hideTimeAxis={true}
                                hideCountAxis={true}
                            />
                        </Flex>
                    </Flex>
                    {loadBarComponent}
                </Flex>
            </Dropdown>
        );
    }
}
